import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { lettersGo, parsePlace, placeKey, type CandidateView, type FindResponse, type Place } from "@joogoos/contracts";

/** What the find route answers with: the shape plus the id of the search it wrote down. */
type Found = FindResponse & { searchId: number };
import { api } from "../api.js";
import { Icon } from "../ui/icons.js";

/**
 * **Find companies** — a panel behind one button, not a screen (23 §the screens).
 *
 * It was the tab's front door, which had it exactly backwards: it is the most expensive thing a person can press
 * and the most optional, because the loop does it on a schedule. So it is one button on Today, and what opens is
 * as short as it can honestly be.
 *
 * Five decisions became two. The group already carries its own words and its own markets, so picking it picks
 * those; a town and a different form of words are there for somebody who wants them and folded away for
 * everybody else.
 */

/** `groupId` starts the search in one group, as a group's own Companies tab does (38). */
export function FindPanel({ onClose, groupId: inGroup = "" }: { onClose: () => void; groupId?: string }) {
  const setup = useQuery({ queryKey: ["customers", "setup"], queryFn: () => api.customersSetup() });
  const sources = useQuery({ queryKey: ["customers", "sources"], queryFn: () => api.customerSources() });
  const client = useQueryClient();

  const [groupId, setGroupId] = useState(inGroup);
  const [country, setCountry] = useState("");
  const [source, setSource] = useState("ask_ai");
  const [region, setRegion] = useState("");
  const [asked, setAsked] = useState("");
  const [more, setMore] = useState(false);
  const [result, setResult] = useState<Found | null>(null);
  const [ticked, setTicked] = useState<Set<string>>(new Set());

  const groups = (setup.data?.groups ?? []).filter((g) => g.state !== "dropped" && g.route !== "own_list");
  const group = groups.find((g) => g.id === groupId);
  // A group's own places, because that is where it is meant to be looked for. A place is a country or one town
  // inside it (ADR 0043); picking one sets both the country the finder searches and the town it narrows to.
  const markets = (group?.countries.length ? group.countries.map(parsePlace) : setup.data?.markets ?? [])
    .filter((p): p is Place => !!p);
  const all = sources.data?.sources ?? [];
  const here = all.find((s) => s.code === source);
  const law = country ? lettersGo(country) : null;
  const ready = Boolean(here?.ready) && Boolean(country) && Boolean(group || asked.trim());

  const find = useMutation({
    mutationFn: () => api.findCompanies({ source, groupId: groupId || undefined, country, region: region || undefined, asked: asked || undefined }),
    onSuccess: (d) => {
      setResult(d);
      setTicked(new Set(d.candidates.filter((c) => !c.known).map(key)));
    },
  });
  const keep = useMutation({
    mutationFn: () => api.keepCompanies({
      searchId: result!.searchId, source, country, groupId: groupId || undefined,
      companies: result!.candidates.filter((c) => ticked.has(key(c))),
    }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["customers", "today"] });
      void client.invalidateQueries({ queryKey: ["customers", "prospects"] });
      onClose();
    },
  });

  const fresh = (result?.candidates ?? []).filter((c) => !c.known);

  return (
    <div className="fixed inset-0 z-40 flex" role="dialog" aria-label="Find companies">
      <button className="flex-1 bg-ink/20" onClick={onClose} aria-label="Close" />
      <div className="w-full max-w-[620px] bg-panel border-l border-line shadow-2xl flex flex-col overflow-y-auto">
        <div className="p-5 flex flex-col gap-3.5">
          <div className="flex items-center gap-2.5">
            <h1 className="text-base font-semibold">Find companies</h1>
            <span className="text-[11.5px] text-muted">nothing joins your list until you add it</span>
            <button onClick={onClose} className="ml-auto p-1 rounded hover:bg-page" aria-label="Close"><Icon name="x" size={14} /></button>
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted">Who to look for</span>
            <div className="flex flex-wrap gap-1.5">
              {groups.map((g) => (
                <button
                  key={g.id}
                  onClick={() => { setGroupId(g.id === groupId ? "" : g.id); setCountry(""); }}
                  className={`px-2.5 py-1 rounded-lg text-xs font-medium border ${groupId === g.id ? "bg-ink text-white border-ink" : "bg-page border-line-2 text-ink-2"}`}
                >{g.name}</button>
              ))}
              {!groups.length && <span className="text-xs text-muted">No groups yet — New group, in the menu, makes one.</span>}
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted">Where</span>
            <div className="flex flex-wrap gap-1.5">
              {markets.map((m) => {
                const on = country === m.country && region === m.town;
                return (
                  <button
                    key={placeKey(m)}
                    onClick={() => { setCountry(on ? "" : m.country); setRegion(on ? "" : m.town); }}
                    className={`px-2.5 py-1 rounded-lg text-xs font-medium border flex items-center gap-1.5 ${on ? "bg-ink text-white border-ink" : "bg-page border-line-2 text-ink-2"}`}
                  ><Icon name="globe" size={12} /> {m.town || MARKET_NAMES[m.country] || m.country}</button>
                );
              })}
              {!markets.length && <span className="text-xs text-muted">Nowhere to look yet — Settings.</span>}
            </div>
            {/* Said before the search is paid for: what is found there gets no letter (ADR 0065). */}
            {law && !law.goes ? (
              <span className="text-xs text-accent leading-relaxed" role="status">
                {law.says} What you find there is kept for an introduction or a call.
              </span>
            ) : null}
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted">How</span>
            <div className="flex flex-wrap gap-1.5">
              {all.map((s) => {
                const wrongCountry = s.countries.length > 0 && country !== "" && !s.countries.includes(country);
                const off = !s.ready || wrongCountry;
                return (
                  <button
                    key={s.code} onClick={() => setSource(s.code)} disabled={off}
                    title={s.needs ? `needs ${s.needs}` : wrongCountry ? `only ${s.countries.join(", ")}` : s.about}
                    className={`px-2.5 py-1 rounded-lg text-xs font-medium border ${source === s.code ? "bg-ink text-white border-ink" : off ? "bg-page border-line-2 text-faint" : "bg-page border-line-2 text-ink-2"}`}
                  >{s.name}</button>
                );
              })}
            </div>
            {here && !here.ready && <span className="text-xs text-bad">Not set up on this box: {here.needs} is missing.</span>}
          </div>

          <button onClick={() => setMore(!more)} className="self-start text-[11.5px] text-muted underline underline-offset-2">
            {more ? "Less" : "A town, or different words"}
          </button>
          {more && (
            <div className="flex flex-col gap-2">
              <input value={region} onChange={(e) => setRegion(e.target.value)} placeholder="a town or region" aria-label="Town or region"
                className="px-2.5 py-1.5 rounded-lg border border-line-2 bg-page text-[13px]" />
              <input value={asked} onChange={(e) => setAsked(e.target.value)} placeholder={group?.looksFor.slice(0, 70) ?? "in your own words"} aria-label="Ask in words"
                className="px-2.5 py-1.5 rounded-lg border border-line-2 bg-page text-[13px]" />
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => find.mutate()} disabled={!ready || find.isPending}
              className="px-3 py-2 rounded-lg bg-ink text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-40"
            >
              <Icon name="search" size={14} />
              {find.isPending ? "Looking…" : <>Find companies <span className="opacity-70 font-medium">· {priceWords(here?.price ?? null)}</span></>}
            </button>
            <span className="text-xs text-muted">Nothing is charged until you press it.</span>
          </div>
          {find.error ? <p className="text-xs text-bad">{String(find.error)}</p> : null}

          {result && (
            <div className="rounded-xl border border-line bg-page overflow-hidden">
              <div className="flex flex-wrap items-center gap-2 px-3.5 py-2 border-b border-line">
                <span className="text-[12.5px] font-semibold">
                  {result.shown} found{fresh.length < result.shown ? ` · ${fresh.length} new` : ""}
                </span>
                <span className="text-[11px] text-muted">this search cost ${result.costUsd}</span>
                <button
                  onClick={() => keep.mutate()} disabled={!ticked.size || keep.isPending}
                  className="ml-auto px-2.5 py-1 rounded-lg bg-ink text-white text-[11.5px] font-semibold disabled:opacity-40"
                >Add {ticked.size}</button>
              </div>
              {result.note ? <p className="px-3.5 py-1.5 text-xs text-muted border-b border-line">{result.note}</p> : null}
              {result.candidates.map((c) => (
                <label key={key(c)} className="flex gap-2.5 items-start px-3.5 py-2 border-b border-line last:border-0 cursor-pointer">
                  <input
                    type="checkbox" checked={ticked.has(key(c))} disabled={c.known} className="mt-1"
                    onChange={() => {
                      const next = new Set(ticked);
                      if (next.has(key(c))) next.delete(key(c)); else next.add(key(c));
                      setTicked(next);
                    }}
                  />
                  <span className="flex flex-col gap-0.5 min-w-0">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="text-[12.5px] font-semibold">{c.name}</span>
                      <span className="text-xs text-muted">{c.domain || "no website"}</span>
                      {c.known && <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-panel border border-line-2 text-muted">already yours</span>}
                    </span>
                    {c.why && <span className="text-xs text-muted leading-relaxed">{c.why}</span>}
                  </span>
                </label>
              ))}
              {!result.candidates.length && <p className="px-3.5 py-2.5 text-[13px] text-muted">Nothing new. Try another town, or different words.</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const key = (c: CandidateView) => c.domain || `${c.name}|${c.placeId}`;

/**
 * What a press costs, on the button rather than beside it (docs/23 §what it costs), from the API's own estimate.
 * The figure was written here, "about 11¢", while a search cost 24¢ and 31¢ (2026-09-29).
 */
export function priceWords(price: string | null): string {
  if (price === null) return "free";
  const cents = Number(price) * 100;
  return cents < 1 ? "under 1¢" : `about ${Math.round(cents)}¢`;
}

const MARKET_NAMES: Record<string, string> = {
  GB: "United Kingdom", IE: "Ireland", FR: "France", ES: "Spain", US: "United States",
  DE: "Germany", IT: "Italy", NL: "Netherlands", BE: "Belgium", PT: "Portugal",
};
